import * as React from 'react';
import { useState } from 'react';

import { useApolloClient } from '@apollo/client';

import ExportDrawer from 'bundles/enterprise-admin-drawers/components/ExportDrawer';
import type { UsageReportV2Options } from 'bundles/enterprise-admin-drawers/utils/EnterpriseEmailReportsAPIUtils';
import { exportProgramsEdsGradebook } from 'bundles/enterprise-admin-program-gradebook/utils/exportGradebookUtils';
import type { GradebookExportOptions } from 'bundles/enterprise-admin-program-gradebook/utils/exportGradebookUtils';
import { useEnterpriseAdminPermissions } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import {
  ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN,
  ENTERPRISE_ADMIN_PERMISSION_VERB,
} from 'bundles/enterprise-admin-roles/types';

import _t from 'i18n!nls/enterprise-admin-drawers';

import { GradebookExportSuccessModal } from './GradebookExportSuccessModal';
import type { ProgramList } from './types';

export type Props = {
  organizationId: string;
  programs: ProgramList;
  onClose: () => void;
};

export const GradebookExportDrawer = ({ organizationId, programs, onClose }: Props) => {
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const { hasPermission } = useEnterpriseAdminPermissions();
  const apolloClient = useApolloClient();

  const programsWithPermission = programs.filter((program) =>
    hasPermission({
      domain: 'PROGRAM',
      domainId: program.id,
      subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.GRADEBOOK,
      verb: ENTERPRISE_ADMIN_PERMISSION_VERB.EXPORT,
    })
  );

  const handleOnExport = (filters: UsageReportV2Options) => {
    const options: GradebookExportOptions = { ...filters } as GradebookExportOptions;

    // All programs are included if none are selected in ExportDrawer
    return exportProgramsEdsGradebook(apolloClient, { programIds: filters.programIds ?? [] }, options).then(() => {
      setShowSuccessModal(true);
    });
  };

  const handleOnCloseModal = () => {
    setShowSuccessModal(false);
    onClose();
  };

  if (showSuccessModal) return <GradebookExportSuccessModal onClose={handleOnCloseModal} />;

  return (
    <ExportDrawer
      onExport={handleOnExport}
      isOpen={true}
      organizationId={organizationId}
      programs={programsWithPermission}
      onRequestClose={onClose}
      filters={{
        programs: [],
        dateRange: null,
        options: {
          oneCsvPerProgram: true,
          includeDeletedMembers: true,
        },
      }}
      title={_t('Gradebook Export')}
    />
  );
};

export default GradebookExportDrawer;
